<template>
    <div v-if="competition?.link" :key="`${$route.params.id}-${$route.params.year}`">
  
      <div class="uk-section uk-section-medium uk-text-center uk-background-muted page-header">
        <div class="uk-light uk-container uk-container-small uk-text-center uk-margin-large-top">
          <router-link :to="competition.link" v-slot="{ href, navigate }" custom>
            <a @click="navigate" :href="href">
              <label>{{ competition.menuTitle }}</label>
            </a>
          </router-link>
          <h1 class="uk-margin-small-top">
            {{ filter.year ? `${filter.year} Finalists` : "Previous Finalists" }}
          </h1>
  
          <template v-if="competition?.years?.edges.length > 0">
            <ul class="uk-subnav uk-subnav-pill uk-flex-center category-filter uk-margin-medium-top" uk-margin>
              <router-link :to="`${competition.link}/finalists`" v-slot="{ href, isActive, navigate }" custom>
                <li :class="{ 'uk-active': isActive }">
                  <a @click="navigate" :href="href">All Finalists</a>
                </li>
              </router-link>
              <template v-for="cyear in competition.years.edges" :key="cyear.node.title">
                <router-link :to="`${competition.link}/finalists/${cyear.node.title}`" v-if="!cyear.node.hideEntriesFilter" v-slot="{ href, isActive, navigate }" custom>
                  <li :class="{ 'uk-active': isActive }">
                    <a @click="navigate" :href="href">{{ cyear.node.title }}</a>
                  </li>
                </router-link>
              </template>
            </ul>
          </template>
  
        </div>
      </div>
  
      <div class="uk-section uk-section-medium uk-background-white">
        <div class="uk-container uk-container-large">
          <h1>
            <b>{{ filter.year ? `${filter.year} ` : '' }}{{ competition.menuTitle }}</b> Finalists
            <span class="uk-badge uk-padding-small" v-if="entries.length">
              {{ entries.length }}
              <span v-if="entries.length < totalCount">&nbsp;/&nbsp;{{ totalCount }}</span>
            </span>
          </h1>
  
          <div class="uk-flex uk-child-width-1-4@l uk-child-width-1-3@m uk-child-width-1-2@s" uk-grid v-if="sortedEntries.length" uk-height-match="target: .match-height">
            <div v-for="entry in sortedEntries" :key="entry.id">
              <EntryCard :entry="entry" />
            </div>
          </div>
  
        </div>
  
        <div class="uk-margin-top uk-text-center loadmore" v-if="!filter.year && sortedEntries.length && loadMore">
          <a class="uk-button uk-button-primary" @click.stop="offset+=limit" :disabled="loading">
            Load more <div uk-spinner v-if="$apollo.loading"></div>
          </a>
        </div>
  
      </div>
  
    </div>
  </template>
  
  <script>
  import gql from 'graphql-tag'
  import EntryCard from '../EntryCard.vue'
  import _ from 'lodash'
  
  const readEntriesQuery = gql`
    query($urlSegment: String!, $limit: Int!, $offset: Int!) {
      readOneCompetition(filter: { urlSegment: { eq: $urlSegment } }) {
        id
        entriesCount
        entries(limit: $limit, offset: $offset) {
          edges {
            node {
              id
              link
              name
              thumbnail
              title
              medium {
                title
              }
              prize {
                id
                title
                prize
                priority
              }
              year {
                title
              }
            }
          }
          pageInfo {
            hasNextPage
          }
        }
      }
    }
  `
  
  const readYearEntriesQuery = gql`
    query($filter: CompetitionYearFilterFields, $limit: Int!, $offset: Int!) {
      readOneCompetitionYear(filter: $filter) {
        id
        entriesCount
        entries(limit: $limit, offset: $offset) {
          edges {
            node {
              id
              link
              title
              name
              thumbnail
              medium {
                title
              }
              prize {
                id
                title
                prize
                priority
              }
              year {
                title
              }
            }
          }
          pageInfo {
            hasNextPage
          }
        }
      }
    }
  `
  
  export default {
    inject: ['state'],
    props: ['competition'],
    data() {
      return {
        entries: [],
        limit: 48,
        offset: 0,
        loadMore: false,
        lastId: 0,
        filter: {
          year: this.$route.params.year || null
        },
        loading: false,
        totalCount: 0,
        fetchingAllPages: false
      }
    },
    watch: {
      '$route'(to) {
        this.offset = 0
        this.lastId = 0
        this.entries = []
        this.loadMore = false
        this.filter.year = to.params.year || null
  
        if (this.filter.year) {
          this.fetchAllYearEntries()
        }
      }
    },
    mounted() {
      if (this.filter.year) {
        this.fetchAllYearEntries()
      }
    },
    apollo: {
      readOneCompetition: {
        query: readEntriesQuery,
        variables() {
          return {
            urlSegment: this.$route.params.id,
            limit: this.limit,
            offset: this.offset
          }
        },
        update(data) {
          const newData = _.map(data.readOneCompetition.entries.edges, "node")
          this.totalCount = data.readOneCompetition.entriesCount
          this.loadMore = data.readOneCompetition.entries.pageInfo.hasNextPage
          if (this.lastId !== newData[0]?.id) {
            this.entries = this.entries.concat(newData)
            this.lastId = newData[0]?.id
          }
        },
        skip() {
          return !!this.$route.params.year
        }
      }
    },
    methods: {
      async fetchAllYearEntries() {
        this.fetchingAllPages = true
        let keepFetching = true
        let pageOffset = 0
  
        while (keepFetching) {
          const { data } = await this.$apollo.query({
            query: readYearEntriesQuery,
            variables: {
              filter: {
                competition: { urlSegment: { eq: this.$route.params.id } },
                year: { eq: this.$route.params.year }
              },
              limit: this.limit,
              offset: pageOffset
            },
            fetchPolicy: 'network-only'
          })
  
          const newData = _.map(data.readOneCompetitionYear.entries.edges, 'node')
          this.totalCount = data.readOneCompetitionYear.entriesCount
          this.entries = this.entries.concat(newData)
          keepFetching = data.readOneCompetitionYear.entries.pageInfo.hasNextPage
          pageOffset += this.limit
        }
  
        this.fetchingAllPages = false
      }
    },
    computed: {
      sortedEntries() {
        if(this.filter.year) {
            return _.orderBy(this.entries, 'title', 'asc')    
        }
        return _.orderBy(this.entries, ['year.title', 'prize.priority'], ['desc', 'desc'])
      }
    },
    components: {
      EntryCard
    },
    updated() {
      if (this.competition && this.competition.title) {
        this.state.setMetaTitle(this.competition.title + ' - Finalists')
      }
    }
  }
  </script>
  